import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { useMutation, useQuery, useLazyQuery, gql } from '@apollo/client';
import { useAuth } from './AuthContext';
import { AUTH_EVENTS } from './AuthContext';
import { useAuthEvents } from '../hooks/useAuthEvents';

const GET_CART_DETAILS = gql`
  query getCartDetails($cartId: String!) {
    cart(cart_id: $cartId) {
      id
      items {
        id
        product {
          id
          name
          sku
          small_image {
            url
          }
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
            }
          }
        }
        quantity
        prices {
          price {
            currency
            value
          }
          total_item_discount {
            currency
            value
          }
        }
      }
      prices {
        grand_total {
          currency
          value
        }
        subtotal_excluding_tax {
          currency
          value
        }
        subtotal_including_tax {
          currency
          value
        }
        applied_taxes {
          amount {
            currency
            value
          }
          label
        }
        discounts {
          amount {
            currency
            value
          }
          label
        }
      }
      shipping_addresses {
        selected_shipping_method {
          amount {
            currency
            value
          }
          carrier_code
          carrier_title
          method_code
          method_title
        }
      }
    }
  }
`;

const CREATE_EMPTY_CART = gql`
  mutation createEmptyCart {
    createEmptyCart
  }
`;

const ADD_SIMPLE_PRODUCTS_TO_CART = gql`
  mutation addSimpleProductsToCart($cartId: String!, $cartItems: [SimpleProductCartItemInput!]!) {
    addSimpleProductsToCart(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const UPDATE_CART_ITEMS = gql`
  mutation updateCartItems($cartId: String!, $cartItems: [CartItemUpdateInput!]!) {
    updateCartItems(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const REMOVE_ITEM_FROM_CART = gql`
  mutation removeItemFromCart($cartId: String!, $cartItemId: Int!) {
    removeItemFromCart(input: {
      cart_id: $cartId
      cart_item_id: $cartItemId
    }) {
      cart {
        id
        items {
          id
          product {
            id
            name
            sku
            small_image {
              url
            }
            price_range {
              maximum_price {
                final_price {
                  currency
                  value
                }
              }
            }
          }
          quantity
          prices {
            price {
              currency
              value
            }
            total_item_discount {
              currency
              value
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
          subtotal_excluding_tax {
            currency
            value
          }
          subtotal_including_tax {
            currency
            value
          }
          applied_taxes {
            amount {
              currency
              value
            }
            label
          }
          discounts {
            amount {
              currency
              value
            }
            label
          }
        }
      }
    }
  }
`;

const MERGE_CARTS = gql`
  mutation mergeCarts($sourceCartId: String!, $destinationCartId: String) {
    mergeCarts(source_cart_id: $sourceCartId, destination_cart_id: $destinationCartId) {
      id
      items {
        id
        product {
          id
          name
          sku
          small_image {
            url
          }
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
            }
          }
        }
        quantity
        prices {
          price {
            currency
            value
          }
          total_item_discount {
            currency
            value
          }
        }
      }
      prices {
        grand_total {
          currency
          value
        }
        subtotal_excluding_tax {
          currency
          value
        }
      }
    }
  }
`;

const GET_CUSTOMER_CART = gql`
  query customerCart {
    customerCart {
      id
      items {
        id
        product {
          id
          name
          sku
          small_image {
            url
          }
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
            }
          }
        }
        quantity
        prices {
          price {
            currency
            value
          }
          total_item_discount {
            currency
            value
          }
        }
      }
      prices {
        grand_total {
          currency
          value
        }
        subtotal_excluding_tax {
          currency
          value
        }
      }
    }
  }
`;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART_ID':
      return { ...state, cartId: action.payload };
    case 'SET_CART_DATA':
      return { ...state, cartData: action.payload };
    case 'SET_IS_MINI_CART_OPEN':
      return { ...state, isMiniCartOpen: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_MERGING':
      return { ...state, merging: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, isAuthenticated, initialLoading: authInitialLoading } = useAuth();

  const [state, dispatch] = useReducer(cartReducer, {
    cartId: null,
    cartData: null,
    isMiniCartOpen: false,
    loading: false,
    merging: false,
    error: null
  });

  // Track if this is the initial mount or a real authentication change
  const isInitialMount = useRef(true);
  const previousAuthState = useRef({ isAuthenticated: false, userId: null });

  const [createEmptyCart] = useMutation(CREATE_EMPTY_CART);
  const [addSimpleProductsToCart] = useMutation(ADD_SIMPLE_PRODUCTS_TO_CART);
  const [updateCartItems] = useMutation(UPDATE_CART_ITEMS);
  const [removeItemFromCart] = useMutation(REMOVE_ITEM_FROM_CART);
  const [mergeCarts] = useMutation(MERGE_CARTS);
  const [getCustomerCart] = useLazyQuery(GET_CUSTOMER_CART);

  // Query cart details when cartId exists
  const { data: cartData, refetch: refetchCart, error: cartError } = useQuery(GET_CART_DETAILS, {
    variables: { cartId: state.cartId },
    skip: !state.cartId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // Handle cart data updates
  useEffect(() => {
    if (cartData?.cart) {
      dispatch({ type: 'SET_CART_DATA', payload: cartData.cart });
    }
  }, [cartData]);

  // Handle cart errors - check for authorization errors, inactive carts, and create new cart
  useEffect(() => {
    if (cartError) {
      console.log('Cart error detected:', cartError);

      // Check if it's an authorization error or inactive cart error
      const isAuthError = cartError.message?.includes('cannot perform operations on cart') ||
                         cartError.graphQLErrors?.some(error =>
                           error.extensions?.category === 'graphql-authorization'
                         );

      const isInactiveCart = cartError.message?.includes("cart isn't active") ||
                            cartError.graphQLErrors?.some(error =>
                              error.extensions?.category === 'graphql-no-such-entity' ||
                              error.message?.includes("cart isn't active")
                            );

      if (isAuthError || isInactiveCart) {
        console.log('Cart error detected (auth or inactive), creating new cart...');

        // Clear the invalid cart ID and create a new cart
        localStorage.removeItem('cartId');
        dispatch({ type: 'SET_CART_ID', payload: null });
        dispatch({ type: 'SET_CART_DATA', payload: null });

        // Create a new empty cart
        createEmptyCart().then(result => {
          const newCartId = result.data.createEmptyCart;
          console.log('New cart created after error:', newCartId);
          localStorage.setItem('cartId', newCartId);
          dispatch({ type: 'SET_CART_ID', payload: newCartId });
          dispatch({ type: 'SET_ERROR', payload: null }); // Clear the error
        }).catch(createError => {
          console.error('Failed to create new cart after error:', createError);
          dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize cart. Please refresh the page.' });
        });
      } else {
        // For other errors, just display the error message
        dispatch({ type: 'SET_ERROR', payload: cartError.message });
      }
    }
  }, [cartError, createEmptyCart]);

  // Initialize cart on mount
  const initializeCart = async (force = false) => {
    let cartId = localStorage.getItem('cartId');

    if (!cartId || force) {
      try {
        const result = await createEmptyCart();
        cartId = result.data.createEmptyCart;
        localStorage.setItem('cartId', cartId);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create cart' });
        return;
      }
    }

    dispatch({ type: 'SET_CART_ID', payload: cartId });
  };

  useEffect(() => {
    initializeCart();
  }, [createEmptyCart]);

  // Listen to auth events to reinitialize cart
  useAuthEvents({
    [AUTH_EVENTS.LOGOUT]: () => {
      initializeCart(true);
    },
    [AUTH_EVENTS.TOKEN_VALIDATED]: (userData) => {
      initializeCart(true);
    },
    [AUTH_EVENTS.TOKEN_INVALID]: () => {
      initializeCart(true);
    },
  });

  const addToCart = async (sku, quantity = 1) => {
    if (!state.cartId) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await addSimpleProductsToCart({
        variables: {
          cartId: state.cartId,
          cartItems: [{
            data: {
              quantity,
              sku
            }
          }]
        }
      });

      // Update local state with response data
      if (result.data?.addSimpleProductsToCart?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.addSimpleProductsToCart.cart });
      }

      // Also refetch to ensure consistency
      await refetchCart();
      dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: true });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (!state.cartId) return;

    try {
      const result = await updateCartItems({
        variables: {
          cartId: state.cartId,
          cartItems: [{
            cart_item_id: cartItemId,
            quantity
          }]
        }
      });

      // Update local state with response data
      if (result.data?.updateCartItems?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.updateCartItems.cart });
      }

      await refetchCart();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const removeItem = async (cartItemId) => {
    if (!state.cartId) return;

    try {
      const result = await removeItemFromCart({
        variables: {
          cartId: state.cartId,
          cartItemId: parseInt(cartItemId)
        }
      });

      // Update local state with response data
      if (result.data?.removeItemFromCart?.cart) {
        dispatch({ type: 'SET_CART_DATA', payload: result.data.removeItemFromCart.cart });
      }

      await refetchCart();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const openMiniCart = () => {
    dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: true });
  };

  const closeMiniCart = () => {
    dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: false });
  };

  const getCartItemsCount = () => {
    if (!state.cartData?.items) return 0;
    return state.cartData.items.reduce((total, item) => total + item.quantity, 0);
  };


  // Handle authentication state changes
  useEffect(() => {
    const handleAuth = async () => {
      // Check if this is a real authentication change, not just initial mount
      const wasNotAuthenticated = !previousAuthState.current.isAuthenticated;
      const isNowAuthenticated = isAuthenticated && user;
      // Use email for comparison since it's more stable than object reference
      const currentUserId = user?.email || user?.id || null;
      const userIdChanged = previousAuthState.current.userId !== currentUserId;


      // Update previous auth state for next comparison
      previousAuthState.current = {
        isAuthenticated,
        userId: currentUserId
      };

      // Only trigger merge if:
      // 1. Not the initial mount AND
      // 2. Auth initial loading is complete AND
      // 3. User just logged in (was not authenticated, now is) OR user changed
      if (!isInitialMount.current && !authInitialLoading && isNowAuthenticated && state.cartId && (wasNotAuthenticated || userIdChanged)) {

        // Set merging state to show loading feedback
        dispatch({ type: 'SET_MERGING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        try {
          // Get customer's existing cart (if any)
          const customerCartResult = await getCustomerCart();
          const customerCart = customerCartResult.data?.customerCart;

          if (customerCart && customerCart.id) {

            // Merge the guest cart into the customer cart
            const mergeResult = await mergeCarts({
              variables: {
                sourceCartId: state.cartId, // Guest cart
                destinationCartId: customerCart.id // Customer cart
              }
            });

            if (mergeResult.data?.mergeCarts) {
              const mergedCart = mergeResult.data.mergeCarts;

              // Update to use the customer cart ID
              localStorage.setItem('cartId', mergedCart.id);
              dispatch({ type: 'SET_CART_ID', payload: mergedCart.id });
              dispatch({ type: 'SET_CART_DATA', payload: mergedCart });
            }
          } else {

            // If customer has no cart, merge into empty destination (creates customer cart)
            const mergeResult = await mergeCarts({
              variables: {
                sourceCartId: state.cartId, // Guest cart
                destinationCartId: null // Let Magento determine customer cart
              }
            });

            if (mergeResult.data?.mergeCarts) {
              const customerCart = mergeResult.data.mergeCarts;

              // Update to use the customer cart ID
              localStorage.setItem('cartId', customerCart.id);
              dispatch({ type: 'SET_CART_ID', payload: customerCart.id });
              dispatch({ type: 'SET_CART_DATA', payload: customerCart });
            }
          }
        } catch (error) {
          console.error('Error during cart merge/conversion:', error);

          // Check if it's a cart authorization error (guest cart became inaccessible after login)
          if (error.message?.includes('cannot perform operations on cart') ||
              error.graphQLErrors?.some(err => err.extensions?.category === 'graphql-authorization')) {

            // Get the customer's cart (should exist from the first query)
            const customerCartResult = await getCustomerCart();
            const customerCart = customerCartResult.data?.customerCart;

            if (customerCart?.id) {
              // Use the customer's existing cart
              localStorage.setItem('cartId', customerCart.id);
              dispatch({ type: 'SET_CART_ID', payload: customerCart.id });
              dispatch({ type: 'SET_CART_DATA', payload: customerCart });
            } else {
              // Create a new empty cart for the customer
              const newCartResult = await createEmptyCart();
              const newCartId = newCartResult.data.createEmptyCart;
              localStorage.setItem('cartId', newCartId);
              dispatch({ type: 'SET_CART_ID', payload: newCartId });
              dispatch({ type: 'SET_CART_DATA', payload: null });
            }
          }
        } finally {
          // Clear merging state
          dispatch({ type: 'SET_MERGING', payload: false });
        }
      } else if (!isInitialMount.current && !isAuthenticated && previousAuthState.current.isAuthenticated) {
        // When user logs out, we might need to create a new guest cart
        // But typically the current cart should continue working as guest cart
        // Only create new cart if current one becomes inaccessible
      }

      // Mark that initial mount is complete only after auth loading is done
      if (isInitialMount.current && !authInitialLoading) {
        isInitialMount.current = false;
      }
    };

    // Only run this effect if we have a cart
    if (state.cartId) {
      handleAuth();
    } else {
      // Even if no cart, mark initial mount as complete and update auth state
      if (isInitialMount.current && !authInitialLoading) {
        isInitialMount.current = false;
        const currentUserId = user?.email || user?.id || null;
        previousAuthState.current = {
          isAuthenticated,
          userId: currentUserId
        };
      }
    }
  }, [isAuthenticated, user, state.cartId, authInitialLoading, getCustomerCart, mergeCarts]); // Run when authentication state changes

  const clearCart = async () => {
    try {
      // Create a new empty cart
      const result = await createEmptyCart();
      const newCartId = result.data.createEmptyCart;

      // Update localStorage and state
      localStorage.setItem('cartId', newCartId);
      dispatch({ type: 'SET_CART_ID', payload: newCartId });
      dispatch({ type: 'SET_CART_DATA', payload: null });
      dispatch({ type: 'SET_IS_MINI_CART_OPEN', payload: false });

    } catch (error) {
      console.error('Error clearing cart:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const value = {
    ...state,
    addToCart,
    updateQuantity,
    removeItem,
    openMiniCart,
    closeMiniCart,
    getCartItemsCount,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};